import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useNotify, useTranslate } from "ra-core";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PageFrame } from "@/tenax/procedures/ProcedurePages";
import { nativeSelectClass } from "@/tenax/tasks/taskData";
import { LendDialog } from "./LendDialog";
import { NewRacketDialog } from "./NewRacketDialog";
import { PrintLabelsDialog } from "./PrintLabelsDialog";
import { RacketCatalogSettings } from "./RacketCatalogSettings";
import { GoodsOutsSection } from "./GoodsOutsSection";
import { LoanConversion } from "./LoanConversion";
import {
  RACKET_STATUSES,
  dashboardCounts,
  fetchRacketByCode,
  findByCode,
  modelOf,
  useRacketOverview,
  useVariants,
  type RacketOverview,
  type RacketStatus,
  type Variant,
} from "./racketData";
import { HolderName } from "./RelatedLoans";
import { ScanField } from "./ScanField";

export const StatusBadge = ({
  status,
  isOverdue = false,
}: {
  status: RacketStatus;
  isOverdue?: boolean;
}) => {
  const translate = useTranslate();
  return (
    <span className="flex flex-wrap gap-1">
      <Badge variant={status === "in_house" ? "secondary" : "outline"}>
        {translate(`tenax.rackets.status.${status}`)}
      </Badge>
      {isOverdue ? (
        <Badge variant="destructive">
          {translate("tenax.rackets.overdue")}
        </Badge>
      ) : null}
    </span>
  );
};

// "Racchette test" page (task 1.10b, D-040): the barcode field always
// listening opens the racket shot; dashboard by model and status; the list
// with holder and days; loans, goods out, conversion and the team's lists.
export const TestRacketList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const { data: rackets = [] } = useRacketOverview();
  const { data: variants = [] } = useVariants();
  const [status, setStatus] = useState<RacketStatus | "">("");
  const [creating, setCreating] = useState(false);
  const [lending, setLending] = useState(false);
  const [printing, setPrinting] = useState(false);
  const shown = status ? rackets.filter((r) => r.status === status) : rackets;

  const onCode = async (code: string) => {
    const racket = findByCode(rackets, code) ?? (await fetchRacketByCode(code));
    if (racket) navigate(`/test_rackets/${racket.id}/show`);
    else
      notify("tenax.rackets.unknown_code", {
        type: "warning",
        messageArgs: { code },
      });
  };

  return (
    <PageFrame title={translate("tenax.rackets.title")}>
      <ScanField
        id="racket-scan"
        label={translate("tenax.rackets.scan")}
        onCode={onCode}
        autoFocus
      />
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => setCreating(true)}>
          {translate("tenax.rackets.new")}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setLending(true)}
        >
          {translate("tenax.rackets.lend.button")}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setPrinting(true)}
        >
          {translate("tenax.rackets.labels.button")}
        </Button>
      </div>

      <Dashboard rackets={rackets} variants={variants} />

      <div className="flex flex-col gap-1">
        <label htmlFor="racket-status-filter" className="text-sm font-medium">
          {translate("tenax.rackets.fields.status")}
        </label>
        <select
          id="racket-status-filter"
          className={nativeSelectClass}
          value={status}
          onChange={(event) =>
            setStatus(event.target.value as RacketStatus | "")
          }
        >
          <option value="">{translate("tenax.rackets.all")}</option>
          {RACKET_STATUSES.map((value) => (
            <option key={value} value={value}>
              {translate(`tenax.rackets.status.${value}`)}
            </option>
          ))}
        </select>
      </div>
      <RacketRows rackets={shown} variants={variants} />

      <Separator />
      <LoanConversion />
      <Separator />
      <GoodsOutsSection />
      <Separator />
      <RacketCatalogSettings />

      {creating ? <NewRacketDialog onClose={() => setCreating(false)} /> : null}
      {printing ? (
        <PrintLabelsDialog
          rackets={rackets}
          variants={variants}
          onClose={() => setPrinting(false)}
        />
      ) : null}
      {lending ? (
        <LendDialog target={{}} onClose={() => setLending(false)} />
      ) : null}
    </PageFrame>
  );
};

const Dashboard = ({
  rackets,
  variants,
}: {
  rackets: RacketOverview[];
  variants: Variant[];
}) => {
  const translate = useTranslate();
  const rows = dashboardCounts(rackets, variants);
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto">
      <table
        aria-label={translate("tenax.rackets.dashboard")}
        className="w-full text-sm"
      >
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="py-1 pr-2 font-medium">
              {translate("tenax.rackets.fields.model")}
            </th>
            {RACKET_STATUSES.map((value) => (
              <th key={value} className="px-2 py-1 text-right font-medium">
                {translate(`tenax.rackets.status.${value}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.model} className="border-t">
              <td className="py-1 pr-2">{row.model}</td>
              {RACKET_STATUSES.map((value) => (
                <td key={value} className="px-2 py-1 text-right tabular-nums">
                  {row.counts[value] || ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RacketRows = ({
  rackets,
  variants,
}: {
  rackets: RacketOverview[];
  variants: Variant[];
}) => {
  const translate = useTranslate();
  if (!rackets.length) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("tenax.rackets.empty")}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {rackets.map((racket) => (
        <li
          key={racket.id}
          aria-label={racket.code}
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm"
        >
          <Link
            to={`/test_rackets/${racket.id}/show`}
            className="font-mono font-medium underline"
          >
            {racket.code}
          </Link>
          <span className="min-w-0 flex-1 truncate">
            {modelOf(racket, variants)}
          </span>
          <StatusBadge status={racket.status} isOverdue={racket.is_overdue} />
          {racket.status === "on_loan" ? (
            <span>
              <HolderName
                contactId={racket.holder_contact_id}
                companyId={racket.holder_company_id}
              />
            </span>
          ) : null}
          <span className="text-muted-foreground">
            {translate("tenax.rackets.days", {
              smart_count: racket.days_since ?? 0,
            })}
          </span>
        </li>
      ))}
    </ul>
  );
};
